import { UserDetails, getUserDetails } from '../user/userInfo';
import moment from 'moment';

export interface DB_Error {
    result: string,
    reason: string,
    exception?: string,
    message?: string
}

export type ValuePiece = Date | null;

export type Value = any;//ValuePiece | [ValuePiece, ValuePiece]

export interface EventCreationDetails {
    start: Value,
    end: Value,
    name: string,
    info: string,
    venue: number,
    slots: number,
    owner: number,
    website_url: string,
    highest_table_number: number,
    lowest_table_number: number,
    rsvp?: boolean
}

export interface EventInfo {
    id: number,
    name: string,
    venue: number,
    startDateTime: Value,
    endDateTime: Value,
    info: string,
    websiteUrl: string,
    slots: number,
    highestTableNumber: number,
    lowestTableNumber: number,
    owner: string,
    seriesId?: number,
    mainMapImage: string,
    venue_Series_Aligned: boolean,
    info_Series_Aligned: boolean,
    start_Time_Series_Aligned: boolean,
    website_URL_Series_Aligned: boolean,
    slots_Series_Aligned: boolean,
    lowest_Table_Number_Series_Aligned: boolean,
    highest_Table_Number_Series_Aligned: boolean,
    main_Map_Image_Series_Aligned: boolean,
    occurence_Number: number,
    travel_Info: string,
    parking_Info: string,
    lodging_Info: string,
    food_Info: string,
    beverages_Info: string,
    private: boolean,
    eType: number,
    is_Cancelled: boolean
}

interface EventInfoDB {
    [key: string] : boolean | number | string | null,
    id: number,
    name: string,
    venue: number | null,
    start_datetime: string,
    end_datetime: string,
    info: string | null,
    website_url: string | null,
    slots: number | null,
    lowest_table_number: number | null,
    highest_table_number: number | null,
    owner: number | null,
    series_id: number | null,
    main_map_image: string | null,
    venue_Series_Aligned: boolean | null,
    info_Series_Aligned: boolean | null,
    start_Time_Series_Aligned: boolean | null,
    website_URL_Series_Aligned: boolean | null,
    slots_Series_Aligned: boolean | null,
    lowest_Table_Number_Series_Aligned: boolean | null,
    highest_Table_Number_Series_Aligned: boolean | null,
    main_Map_Image_Series_Aligned: boolean | null,
    occurence_Number: number,
    travel_Info: string | null,
    parking_Info: string | null,
    lodging_Info: string | null,
    food_Info: string | null,
    beverages_Info: string | null,
    private: boolean | null,
    eType: number | null,
    is_Cancelled: boolean | null
}

export async function makeEventInfo(res: EventInfoDB): Promise<EventInfo> {
    const default_values : {[key: string]: number} = {
        venue: 1,
        slots: 20,
        lowest_table_number: 1,
        highest_table_number: 4,
        eType: 2
    }

    for (let k of Object.keys(default_values)) {
        if (!res.hasOwnProperty(k))
            console.warn(`Event id ${res.id} missing ${k}. Using default value ${default_values[k]}.`) 
    }

    const startTimegmt = moment.tz(res.start_datetime, "GMT");
    const endTimegmt = moment.tz(res.end_datetime, "GMT");

    const eventInfo: EventInfo = {
        id: res.id,
        name: res.name,
        venue: (res.venue !== null) ? res.venue : default_values.venue,
        startDateTime: startTimegmt.clone().tz('America/New_York'),
        endDateTime: endTimegmt.clone().tz('America/New_York'),
        info: (res.info !== null) ? res.info : "",
        websiteUrl: (res.website_url !== null) ? res.website_url : "",
        slots: (res.slots !== null) ? res.slots : default_values.slots,
        lowestTableNumber: (res.lowest_table_number !== null) ? res.lowest_table_number : default_values.lowest_table_number,
        highestTableNumber: (res.highest_table_number !== null) ? res.highest_table_number : default_values.highest_table_number,
        owner: (res.owner !== null && res.owner !== undefined) ? await getUserDetails(res.owner).then(usr => (usr === undefined ? "" : usr.user_name)) : "",
        ...(res.series_id) && {seriesId: res.series_id},
        mainMapImage: (res.main_map_image !== null) ? res.main_map_image : "",
        venue_Series_Aligned: (res.venue_Series_Aligned !== null) ? res.venue_Series_Aligned : false,
        info_Series_Aligned: (res.info_Series_Aligned !== null) ? res.info_Series_Aligned : false,
        start_Time_Series_Aligned: (res.start_Time_Series_Aligned !== null) ? res.start_Time_Series_Aligned : false,
        website_URL_Series_Aligned: (res.website_URL_Series_Aligned !== null) ? res.website_URL_Series_Aligned : false,
        slots_Series_Aligned: (res.slots_Series_Aligned !== null) ? res.slots_Series_Aligned : false,
        lowest_Table_Number_Series_Aligned: (res.lowest_Table_Number_Series_Aligned !== null) ? res.lowest_Table_Number_Series_Aligned : false,
        highest_Table_Number_Series_Aligned: (res.highest_Table_Number_Series_Aligned !== null) ? res.highest_Table_Number_Series_Aligned : false,
        main_Map_Image_Series_Aligned: (res.main_Map_Image_Series_Aligned !== null) ? res.main_Map_Image_Series_Aligned : false,
        occurence_Number: res.occurence_Number,
        travel_Info: (res.travel_Info !== null) ? res.travel_Info : "",
        parking_Info: (res.parking_Info !== null) ? res.parking_Info : "",
        lodging_Info: (res.lodging_Info !== null) ? res.lodging_Info : "",
        food_Info: (res.food_Info !== null) ? res.food_Info : "",
        beverages_Info: (res.beverages_Info !== null) ? res.beverages_Info : "",
        private: (res.private !== null) ? res.private : false,
        eType: (res.eType !== null) ? res.eType : default_values.eType,
        is_Cancelled: (res.is_Cancelled !== null) ? res.is_Cancelled : false
    }

    return eventInfo;
}

export interface getCalendarEventsResponse {
    result: string,
    data: EventInfoDB[]
}

function isGetCalendarEventsResponse(resp: getCalendarEventsResponse | DB_Error): resp is getCalendarEventsResponse {
    return (resp as getCalendarEventsResponse).result === 'success';
}

interface getSpecificEventResponse {
    result: string,
    data: EventInfoDB
}

function isGetSpecificEventResponse(resp: getSpecificEventResponse | DB_Error): resp is getSpecificEventResponse {
    return (resp as getSpecificEventResponse).result === 'success';
}

export interface eventRsvpList {
    count: number,
    data: UserDetails[]
}

interface getRsvpListResponse extends eventRsvpList {
    result: string
}

function isGetRsvpListResponse(resp: getRsvpListResponse | DB_Error): resp is getRsvpListResponse {
    return (resp as getRsvpListResponse).result === 'success';
}

export interface EventType{
    id: number,
    name: string
}





export const getCalendarEvents = async (from: Date, to: Date): Promise<EventInfo[] | undefined> => {
    console.log("Getting all events");
    const requestOptions = {
        method: 'GET',
        headers: { 'Authorization': 'Basic YnV6ejpmcjBudGllcg==' }
    }

    const propRequestBody = {
        "propagation_range" : moment(to).format('YYYY-MM-DD').toString(), 
        "locale" : "America/New_York" 
    }

    const propRequestOptions = {
        method: 'POST',
        headers: { 'Authorization': 'Basic YnV6ejpmcjBudGllcg==' , 'Content-Type': 'application/json' },
        body: JSON.stringify(propRequestBody)
    }

    let queryParams = from === undefined ? "" : "from=" + encodeURIComponent(from.toISOString()) + '&';
    queryParams += to === undefined ? "" : "to=" + encodeURIComponent(to.toISOString());
    console.log("queryParams: " + queryParams);

    const propresult = await fetch(process.env.REACT_APP_REFLEBULA + '/a1/tga/event2/series/prop/all', propRequestOptions)

    console.log(propresult.status)


    return fetch(process.env.REACT_APP_REFLEBULA + '/a1/tga/cal/events' + (queryParams && "?" + queryParams), requestOptions)
        .then((response) => {
            if (!response.ok) {
                console.log('Fetch Error in getCalendarEvents')
                console.log(`HTTP Response Code: ${response.status}`)
                throw ('Bad Fetch');
            } else {
                return response;
            }
        })
        .then(res => res.json())
        .then((res: DB_Error | getCalendarEventsResponse) => {
            if (isGetCalendarEventsResponse(res)) {
                return res.data;
            } else {
                console.log(`Backend Error in getCalendarEvents Reason ${res.reason}`)
                res.exception && console.log(`Exception ${res.exception}`)
                res.message && console.log(`Message ${res.message}`)
                return undefined;
            }
        })
        .then((res) => {
            if (!res) {
                return;
            }
            return Promise.all(res.map(makeEventInfo));
        })
        .catch((reason) => {
            console.log('Fetch Exception in getCalendarEvents')
            console.log(`Reason: ${reason}`)
            return undefined;
        })
}





export const getSpecificEvent = (eventId: number): Promise<EventInfo | undefined> => {
    const requestOptions = {
        method: 'GET',
        headers: { 'Authorization': 'Basic YnV6ejpmcjBudGllcg==' }
    }
    return fetch(process.env.REACT_APP_REFLEBULA + `/a1/tga/event?id=${encodeURIComponent(eventId)}`, requestOptions)
        .then((response) => {
            if (!response.ok) {
                console.log('Fetch Error in getSpecificEvent')
                console.log(`HTTP Response Code: ${response.status}`)
                throw ('Bad Fetch');
            } else {
                return response;
            }
        })
        .then(res => res.json())
        .then((res: DB_Error | getSpecificEventResponse) => {
            if (isGetSpecificEventResponse(res)) {
                return res.data;
            } else {
                console.log(`Backend Error in getSpecificEvent Reason ${res.reason}`)
                res.exception && console.log(`Exception ${res.exception}`)
                res.message && console.log(`Message ${res.message}`)
                return undefined;
            }
        })
        .then( async (res) => {
            if (!res) {
                return;
            }
            const evtInfo = await makeEventInfo(res);
            return evtInfo;
        })
        .catch((reason) => {
            console.log('Fetch Exception in getSpecificEvent')
            console.log(`Reason: ${reason}`)
            return undefined;
        })
}

export const getRSVPsToEvent = (eventId: number): Promise<eventRsvpList | undefined> => {
    const requestOptions = {
        method: 'GET',
        headers: { 'Authorization': 'Basic YnV6ejpmcjBudGllcg==' },
    }
    return fetch(process.env.REACT_APP_REFLEBULA + '/a1/tga/eventUsers/event/' + eventId, requestOptions)
        .then((response) => {
            if (!response.ok) {
                console.log('Fetch Error in getRSVPsToEvent')
                console.log(`HTTP Response Code: ${response.status}`)
                throw ('Bad Fetch');
            } else {
                return response;
            }
        })
        .then(res => res.json())
        .then((res: DB_Error | getRsvpListResponse) => {
            if (isGetRsvpListResponse(res)) {
                return res as eventRsvpList;
            } else {
                console.log(`Backend Error in getRSVPsToEvent Reason ${res.reason}`)
                res.exception && console.log(`Exception ${res.exception}`)
                res.message && console.log(`Message ${res.message}`)
                return undefined;
            }
        })
        .catch((reason) => {
            console.log('Fetch Exception in getRSVPsToEvent')
            console.log(`Reason: ${reason}`)
            return undefined;
        })
}

export const createEvent = (input: EventCreationDetails, authToken: string): Promise<boolean> => {
    const requestBody = {
        start_time: input.start,
        end_time: input.end,
        ...input
    }
    const requestOptions = {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
    }
    return fetch(process.env.REACT_APP_REFLEBULA + '/a1/tga/event2/create', requestOptions)
        .then(res => res.json())
        .then((res) => {
            if (res.status && res.status === `success`) {
                return true;
            } else {
                console.log(`Backend Error in CreateEvent`);
                res.reason && console.log(`Reason: ${res.reason}`);
                return false;
            }
        })
        .catch((reason) => {
            console.log('Fetch Exception in CreateEvent')
            console.log(`Reason: ${reason}`)
            return false;
        })
}

export const updateEvent = (input: EventInfo, authToken: string): Promise<boolean> => {
    const requestOptions = {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(input)
    }
    return fetch(process.env.REACT_APP_REFLEBULA + '/a1/tga/event2/update' , requestOptions)
        .then(res => res.json())
        .then((res) => {
            if (res.result && res.result === `success`) {
                return true;
            } else {
                console.log(`Backend Error in UpdateVenue`);
                res.reason && console.log(`Reason: ${res.reason}`);
                res.exception && console.log(`Exception ${res.exception}`)
                res.message && console.log(`Message ${res.message}`)
                return false;
            }
        })
        .catch((reason) => {
            console.log('Fetch Exception in UpdateVenue')
            console.log(`Reason: ${reason}`)
            return false;
        })
}

export const isUserOwnerofEvent = (eid: number, uid: number, authToken: string): Promise<boolean> => {
    const requestBody = {
        uid: uid
    }
    const requestOptions = {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
    }
    return fetch(process.env.REACT_APP_REFLEBULA + `/tga/event/ocheck/` + eid, requestOptions)
        .then(res => res.json())
        .then((res) => {
            if (res.status && res.status === `success`) {
                return res.result;
            } else {
                console.log(`Backend Error in UpdateVenue`);
                res.reason && console.log(`Reason: ${res.reason}`);
                res.exception && console.log(`Exception ${res.exception}`)
                res.message && console.log(`Message ${res.message}`)
                return false;
            }
        })
        .catch((reason) => {
            console.log('Fetch Exception in UpdateVenue')
            console.log(`Reason: ${reason}`)
            return false;
        })
}